import domready from "domready";
import React, { Suspense } from "react";
import { render } from "react-dom";
import { Provider } from "react-redux";
import { RawIntlProvider } from "react-intl";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import randomString from "random-string";
import Logger from "./helpers/Logger";
import debug from "debug";
import RoomClient from "./helpers/RoomClient";
import MessagingClient from "./helpers/MessagingClient";
import RoomContext from "./context/RoomContext";
import Notifications from "./components/Notifications/Notifications";
import CallView from "./components/Common/CallView";
import deviceInfo from "./utils/deviceInfo";
import * as meActions from "./actions/meActions";
import { getClientInfo } from "./helpers/remoteControl/clientInfo";
import LoadingView from "./components/Common/LoadingView";
import { PersistGate } from "redux-persist/lib/integration/react";
import { persistor, store } from "./store";
import { SnackbarProvider } from "notistack";
import * as serviceWorker from "./serviceWorker";
import { ReactLazyPreload } from "./components/Common/ReactLazyPreload";
import AuthService from "./api/AuthService";
import CssBaseline from "@material-ui/core/CssBaseline";
import ThemeWrapper from "./context/ThemeWrapper";

import trLocale from "date-fns/locale/tr";
import ruLocale from "date-fns/locale/ru";
import enLocale from "date-fns/locale/en-US";

import "./index.css";
import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { generateIntl } from "./intl";
import moment from "moment";
import "moment/locale/tr";
import LightRoom from "./components/LightRoom";
import ImageViewer from "./helpers/ImageViewer";
import { resolveLanguage } from "./utils/language";

const localeMap = {
  en: enLocale,
  tr: trLocale,
  ru: ruLocale,
};

const App = ReactLazyPreload(() =>
  import(/* webpackChunkName: "app" */ "./components/App")
);

if (
  process.env.REACT_APP_DEBUG === "*" ||
  process.env.NODE_ENV !== "production"
) {
  debug.enable("* -engine* -socket* -RIE* *WARN* *ERROR*");
}

const logger = new Logger();
const intl = generateIntl();

let roomClient;
let messagingClient;
let apiServiceClient;

RoomClient.init({ store, intl });
MessagingClient.init({ store, intl });

const Router = BrowserRouter;

domready(() => {
  logger.debug("DOM ready");

  run();
});

moment.locale(resolveLanguage(store.getState().user.userLanguage));

function run() {
  logger.debug("run() [environment:%s]", process.env.NODE_ENV);

  const peerId = randomString({ length: 8 }).toLowerCase();
  const urlParser = new URL(window.location);
  const parameters = urlParser.searchParams;

  const accessCode = parameters.get("code");
  const produce = parameters.get("produce") !== "false";
  const useSimulcast = parameters.get("simulcast") === "true";
  const useSharingSimulcast = parameters.get("sharingSimulcast") === "true";
  const forceTcp = parameters.get("forceTcp") === "true";
  const displayName = parameters.get("displayName");
  const muted = parameters.get("muted") === "true";

  // Get current device.
  const device = deviceInfo();

  store.dispatch(
    meActions.setMe({
      peerId,
    })
  );
  store.dispatch(meActions.setDesktopCapabilities(getClientInfo()));

  roomClient = new RoomClient({
    peerId,
    accessCode,
    device,
    useSimulcast,
    useSharingSimulcast,
    produce,
    forceTcp,
    displayName,
    muted,
  });

  messagingClient = new MessagingClient({});

  AuthService.init(store, messagingClient, roomClient, intl);
  apiServiceClient = new AuthService();

  render(
    <Provider store={store}>
      <ThemeWrapper>
        <PersistGate loading={<LoadingView />} persistor={persistor}>
          <RoomContext.Provider
            value={{ roomClient, apiServiceClient, messagingClient }}
          >
            <SnackbarProvider>
              <MuiPickersUtilsProvider
                locale={
                  localeMap[
                    resolveLanguage(store.getState().user.userLanguage)
                  ] || trLocale
                }
                utils={DateFnsUtils}
              >
                <Router>
                  <Suspense fallback={<LoadingView />}>
                    <React.Fragment>
                      <CssBaseline />
                      <Switch>
                        <Route exact path="/">
                          <App />
                        </Route>
                        <Route path="/mobile/:id">
                          <LightRoom />
                        </Route>
                        <Route path="/:id">
                          <App />
                        </Route>
                      </Switch>
                    </React.Fragment>
                  </Suspense>
                  <CallView />
                  <ImageViewer />
                </Router>
              </MuiPickersUtilsProvider>
              {!window.location.href.includes("mobile") && <Notifications />}
            </SnackbarProvider>
          </RoomContext.Provider>
        </PersistGate>
      </ThemeWrapper>
    </Provider>,
    document.getElementById("dekob-conference")
  );
}

serviceWorker.unregister();
